import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ShieldAlert, ShieldCheck, Loader2, Lock, Scale, FileSearch } from "lucide-react";
import { FIRM } from "@/lib/firm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

export default function FinancialCrimesUnit() {
  const search = new URLSearchParams(useLocation().search);
  const [eng, setEng] = useState(null);
  const [f, setF] = useState({ victim_name: "", victim_entity: "", victim_email: "", victim_phone: "", victim_address: "", fraud_type: "", incident_date: "", loss_amount: "", method: "", narrative: "", suspects: "" });
  const [deliveryFax, setDeliveryFax] = useState("");
  const [signer, setSigner] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [paid, setPaid] = useState(false);
  const [checking, setChecking] = useState(false);
  const [caseToken, setCaseToken] = useState("");
  const [caseIdPaid, setCaseIdPaid] = useState("");

  useEffect(() => { axios.get(`${API}/financial-crimes/engagement`).then((r) => setEng(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const sess = search.get("session");
    if (search.get("paid") === "1" && sess) {
      setChecking(true);
      axios.get(`${API}/financial-crimes/status/${sess}`).then((r) => {
        setPaid(!!r.data.paid);
        setCaseToken(r.data.case_token || "");
        setCaseIdPaid(r.data.case_id || search.get("case") || "");
      }).finally(() => setChecking(false));
    }
  }, []); // eslint-disable-line

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    setErr("");
    if (!f.victim_name && !f.victim_entity) { setErr("Enter your name or business."); return; }
    if (!signer.trim() || !consent) { setErr("Type your name and accept the Engagement & Authorization to continue."); return; }
    setBusy(true);
    try {
      const report = {
        victim_name: f.victim_name, victim_entity: f.victim_entity, victim_email: f.victim_email,
        victim_phone: f.victim_phone, victim_address: f.victim_address, fraud_type: f.fraud_type,
        incident_date: f.incident_date, loss_amount: f.loss_amount === "" ? null : Number(f.loss_amount),
        method: f.method, narrative: f.narrative,
        suspects: f.suspects.split("\n").filter(Boolean).map((line) => ({ name: line.trim() })),
        agencies: ["ic3", "ftc", "sec", "finra", "police", "bank"],
      };
      const r = await axios.post(`${API}/financial-crimes/intake`, { report, signer_name: signer.trim(), consent, delivery_fax: deliveryFax.trim() });
      const origin = window.location.origin;
      const c = await axios.post(`${API}/financial-crimes/checkout`, { case_id: r.data.case_id, origin_url: origin });
      window.location.href = c.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start your engagement. Please try again.");
      setBusy(false);
    }
  };

  const inp = { width: "100%", background: "#0F1A14", border: "1px solid #1f4030", borderRadius: 8, padding: "10px 12px", color: "#E7F3EE", fontSize: 13, marginBottom: 10 };
  const lbl = { display: "block", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".1em", color: "#8fae9f", marginBottom: 4 };

  return (
    <div style={{ background: "#06160F", minHeight: "100vh", color: "#E7F3EE", fontFamily: "'Manrope',system-ui,sans-serif" }} data-testid="fciu-page">
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "44px 20px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#6FD3AE", fontWeight: 800, letterSpacing: ".1em", fontSize: 12, textTransform: "uppercase", marginBottom: 10 }}>
          <ShieldAlert size={18} /> {FIRM.name} · Financial Crimes Investigations Unit
        </div>
        <h1 style={{ fontFamily: "Georgia,serif", fontSize: 32, margin: "0 0 8px" }}>Report fraud. Get a filing-ready case packet.</h1>
        <p style={{ color: "#9DB8AD", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          Our unit organizes your evidence into a professional Fraud Report &amp; Evidence Packet and prepares and submits
          administrative complaints to the agencies that investigate financial crime (FBI IC3, FTC, SEC, FINRA, CFPB, your state
          Attorney General and local police), and coordinates with your bank on wire-recall/ACH recovery.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
          {[[FileSearch, "Evidence organized"], [Scale, "Complaints prepared & filed"], [Lock, "Bank recovery coordinated"]].map(([Ic, t], i) => (
            <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#0A241A", border: "1px solid #14503B", borderRadius: 999, padding: "7px 14px", fontSize: 12, color: "#C8DAD2" }}><Ic size={14} color="#6FD3AE" /> {t}</div>
          ))}
        </div>

        <div style={{ background: "#0A241A", border: "1px solid #14503B", borderRadius: 14, padding: "18px 20px", marginBottom: 22 }} data-testid="fciu-included">
          <div style={{ fontSize: 11, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".12em", marginBottom: 10 }}>What your {money(eng?.fee || 850)} engagement includes</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 18px" }}>
            {[
              "Professional Fraud Report & Evidence Packet (your documents attached as exhibits)",
              "Sworn Affidavit (penalty-of-perjury, notary jurat) for civil/criminal referral",
              "Complaints prepared & filed: IC3, FTC, SEC, FINRA, IRS-CI, Secret Service, state AG, police",
              "Demand & Cease-and-Desist letter to the subject(s)",
              "Bank wire-recall / ACH-dispute and chargeback request letters",
              "FINRA BrokerCheck / SEC EDGAR verification worksheet",
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, color: "#C8DAD2" }}><ShieldCheck size={15} color="#6FD3AE" style={{ flex: "0 0 auto", marginTop: 1 }} /> {t}</div>
            ))}
          </div>
        </div>

        {paid ? (
          <div data-testid="fciu-paid" style={{ background: "#07251a", border: "1px solid #14503B", borderRadius: 14, padding: 26 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#6FD3AE", fontWeight: 800, fontSize: 18 }}><ShieldCheck size={22} /> You're engaged with the FCIU.</div>
            <p style={{ color: "#C8DAD2", fontSize: 14, marginTop: 10, lineHeight: 1.6 }}>Payment received and your authorization is on file. Your complete case kit — Fraud Report &amp; Evidence Packet, sworn affidavit, and ready-to-send letters — is being <b>assembled automatically and emailed to you now</b>{deliveryFax ? ", and faxed to the number you provided" : ""}. Check your inbox shortly for your case reference and documents.</p>
            {caseToken && caseIdPaid && (
              <a href={`/fciu-case/${caseIdPaid}?t=${encodeURIComponent(caseToken)}`} data-testid="fciu-portal-link"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 14, background: "#6FD3AE", color: "#06160F", textDecoration: "none", padding: "11px 18px", borderRadius: 10, fontSize: 13, fontWeight: 800 }}>
                <ShieldCheck size={16} /> Open your case portal
              </a>
            )}
          </div>
        ) : (
          <div style={{ background: "#0A241A", border: "1px solid #14503B", borderRadius: 14, padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 14 }}>Intake</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={lbl}>Your name</label><input style={inp} value={f.victim_name} onChange={set("victim_name")} data-testid="fciu-name" /></div>
              <div><label style={lbl}>Business / entity</label><input style={inp} value={f.victim_entity} onChange={set("victim_entity")} /></div>
              <div><label style={lbl}>Email</label><input style={inp} value={f.victim_email} onChange={set("victim_email")} /></div>
              <div><label style={lbl}>Phone</label><input style={inp} value={f.victim_phone} onChange={set("victim_phone")} /></div>
              <div><label style={lbl}>Type of fraud</label><input style={inp} value={f.fraud_type} onChange={set("fraud_type")} /></div>
              <div><label style={lbl}>Amount lost ($)</label><input type="number" style={inp} value={f.loss_amount} onChange={set("loss_amount")} /></div>
            </div>
            <label style={lbl}>What happened?</label>
            <textarea rows={4} style={{ ...inp, resize: "vertical" }} value={f.narrative} onChange={set("narrative")} data-testid="fciu-narrative" />
            <label style={lbl}>People / companies involved (one per line)</label>
            <textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.suspects} onChange={set("suspects")} />
            <label style={lbl}>Delivery fax (optional) — where we fax your completed kit (e.g., your bank's fraud line or your attorney)</label>
            <input style={inp} value={deliveryFax} onChange={(e) => setDeliveryFax(e.target.value)} data-testid="fciu-fax" placeholder="+1 555 555 5555" />

            <div style={{ fontSize: 12, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".1em", margin: "14px 0 8px" }}>Engagement &amp; Authorization</div>
            <div data-testid="fciu-engagement" style={{ maxHeight: 180, overflowY: "auto", background: "#0F1A14", border: "1px solid #1f4030", borderRadius: 8, padding: "12px 14px", fontSize: 11.5, color: "#C8DAD2", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {eng ? eng.engagement_text : "Loading engagement terms…"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
              <div><label style={lbl}>Type your full name to sign</label><input style={inp} value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="fciu-signer" placeholder="/s/ Your Name" /></div>
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: "#C8DAD2", cursor: "pointer" }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="fciu-consent" />
              <span>I accept the Engagement &amp; Authorization, authorize {FIRM.name} to prepare and submit fraud complaints on my behalf, and agree to the {money(eng?.fee || 850)} service fee. I understand this is not legal representation and no outcome is guaranteed.</span>
            </label>

            {err && <div data-testid="fciu-error" style={{ background: "#2a1212", border: "1px solid #7A3A3A", color: "#FF9A9A", padding: "10px 14px", borderRadius: 8, margin: "12px 0", fontSize: 13 }}>{err}</div>}

            <button onClick={submit} disabled={busy || checking} data-testid="fciu-pay"
              style={{ marginTop: 14, width: "100%", background: "#6FD3AE", color: "#06160F", border: "none", borderRadius: 10, padding: "15px", fontSize: 14, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {busy ? <><Loader2 className="animate-spin" size={16} /> Redirecting to secure checkout…</> : checking ? <><Loader2 className="animate-spin" size={16} /> Confirming payment…</> : <><ShieldCheck size={16} /> Engage &amp; Pay {money(eng?.fee || 850)}</>}
            </button>
            <p style={{ fontSize: 11, color: "#7FA496", marginTop: 10, textAlign: "center" }}>Secure checkout via Stripe. {FIRM.name} is not a law firm and does not provide legal advice, surveillance, or people-location services.</p>
          </div>
        )}
      </div>
    </div>
  );
}
